import React, { FC } from "react";
import { Modal, ModalBody } from "reactstrap";
import { FiX } from "react-icons/fi";

import { AdventDoor } from "apps/user/learner/api/types";
import {
  categoryLabelKey,
  ContentDestination,
  contentCtaKey,
  doorIcon,
  parseContentDestination,
} from "apps/user/learner/lib/advent-doors";
import {
  DoorMechanic,
  DoorMechanicResponse,
} from "apps/user/learner/lib/advent-door-mechanics";
import { useTranslation } from "lib/i18n/use-translation";

import DoorInvite from "./DoorInvite";
import DoorMechanicPanel from "./DoorMechanicPanel";
import OriButton from "./OriButton";
import RecapLookBack from "./RecapLookBack";
import styles from "../december-doors.module.scss";

interface DoorModalProps {
  door: AdventDoor | null;
  /** The open door's parsed mechanic; set whenever `door` is. */
  mechanic: DoorMechanic | null;
  /** Every door in the campaign, for the recap door's look-back. */
  doors: AdventDoor[];
  isOriEnabled: boolean;
  onClose: () => void;
  onOpenContent: (door: AdventDoor, destination: ContentDestination) => void;
  onChatToOri: (door: AdventDoor) => void;
  /** Records the door's completion; rejects when the server refuses it. */
  onComplete: (
    door: AdventDoor,
    response: DoorMechanicResponse | null
  ) => Promise<void>;
}

const DoorModal: FC<DoorModalProps> = ({
  door,
  mechanic,
  doors,
  isOriEnabled,
  onClose,
  onOpenContent,
  onChatToOri,
  onComplete,
}) => {
  const { t } = useTranslation(["decemberDoors"]);

  const renderMechanic = (current: AdventDoor) => {
    if (!mechanic) {
      throw new Error(`Door ${current.dayNumber} has no parsed mechanic`);
    }
    return (
      <DoorMechanicPanel
        key={current.id}
        door={current}
        mechanic={mechanic}
        onComplete={(response) => onComplete(current, response)}
      />
    );
  };

  // Sits under the mechanic and above Ori, once the door is open.
  const renderInvite = (current: AdventDoor) => {
    if (!current.isOpened || !current.invite) return null;
    return (
      <DoorInvite
        key={`invite-${current.id}`}
        door={current}
        invite={current.invite}
      />
    );
  };

  const renderWhyThisHelps = (current: AdventDoor) => {
    if (!current.whyThisHelps) return null;
    return (
      <>
        <h3 className={styles["modal-subheading"]}>
          {t("decemberDoors:whyThisHelps")}
        </h3>
        <p className={styles["modal-body-text"]}>{current.whyThisHelps}</p>
      </>
    );
  };

  const renderBody = (current: AdventDoor) => {
    switch (current.doorType) {
      case "CONTENT": {
        if (current.contentId && current.contentAvailable) {
          const destination = parseContentDestination(current.contentId);
          return (
            <>
              {renderWhyThisHelps(current)}
              <button
                type="button"
                className={styles["modal-cta"]}
                onClick={() => onOpenContent(current, destination)}
                data-testid="door-cta-content"
              >
                {t(contentCtaKey(destination.target))}
              </button>
            </>
          );
        }
        return (
          <>
            <p className={styles["modal-note"]}>
              {t("decemberDoors:contentUnavailable")}
            </p>
            {renderWhyThisHelps(current)}
          </>
        );
      }
      case "SELF":
        return (
          <>
            {renderWhyThisHelps(current)}
            {renderMechanic(current)}
            {renderInvite(current)}
            {isOriEnabled && current.oriCtaLabel && current.oriPrompt && (
              <OriButton
                label={current.oriCtaLabel}
                onClick={() => onChatToOri(current)}
                testId="door-cta-ori"
              />
            )}
          </>
        );
      case "RECAP":
        return (
          <RecapLookBack
            door={current}
            doors={doors}
            isOriEnabled={isOriEnabled}
            onChatToOri={onChatToOri}
          />
        );
      case "SOCIAL":
        return renderWhyThisHelps(current);
      case "NOTHING":
        return null;
      default:
        throw new Error(`Unknown door type "${current.doorType}"`);
    }
  };

  const Icon = door ? doorIcon(door) : null;

  return (
    <Modal
      isOpen={door !== null}
      toggle={onClose}
      centered
      returnFocusAfterClose
      className={styles.modal}
      contentClassName={styles["modal-content"]}
      labelledBy="december-door-title"
    >
      {door && (
        <ModalBody className={styles["modal-body"]} data-testid="door-modal">
          <button
            type="button"
            className={styles["modal-close"]}
            aria-label={t("decemberDoors:door.close")}
            onClick={onClose}
          >
            <FiX aria-hidden />
          </button>
          {Icon && (
            <span className={styles["modal-icon"]} aria-hidden>
              <Icon />
            </span>
          )}
          <p className={styles["modal-eyebrow"]}>
            {t("decemberDoors:sheet.eyebrow", {
              day: door.dayNumber,
              category: t(categoryLabelKey(door.category)),
            })}
          </p>
          <h2 id="december-door-title" className={styles["modal-title"]}>
            {door.title}
          </h2>
          <p className={styles["modal-subtitle"]}>{door.subtitle}</p>
          {renderBody(door)}
          {door.doorType !== "SELF" && renderMechanic(door)}
          {door.doorType !== "SELF" && renderInvite(door)}
        </ModalBody>
      )}
    </Modal>
  );
};

export default DoorModal;
